// First-login welcome banner — Phase 7.
//
// Fires once when `user_first_login_ever` lands in the FreshAchievementsContext
// queue. Dismissal is persisted in localStorage so the banner only shows once
// per user/device. The banner suppresses the generic toast for this key (see
// FreshAchievementsToastHost's suppression list), so it is the sole celebration
// surface for the welcome moment.
//
// CTA "Open Activity" dispatches the `ACTIVITY_DOCK_OPEN_EVENT` so the floating
// dock auto-expands. Auto-dismiss after 30s if untouched (still sets the
// localStorage flag so the banner doesn't return next session).
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles, X } from "lucide-react";
import { useFreshAchievementsOptional } from "@/contexts/FreshAchievementsContext";
import { ACTIVITY_DOCK_OPEN_EVENT } from "@/components/activity/ActivityFloatingDock";

const STORAGE_KEY = "bb.welcome_banner.first_login_ever.v1";
const AUTO_DISMISS_MS = 30_000;
const WELCOME_KEY = "user_first_login_ever";

function readDismissedFlag(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissedFlag(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    /* swallowed — localStorage may be disabled */
  }
}

export function FirstLoginWelcomeBanner() {
  const ctx = useFreshAchievementsOptional();
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissedFlag());

  const welcomeRow = useMemo(() => {
    if (!ctx) return null;
    return ctx.queue.find((r) => r.achievement_key === WELCOME_KEY) ?? null;
  }, [ctx]);

  const shouldShow = !!welcomeRow && !dismissed;

  const handleDismiss = useCallback(() => {
    writeDismissedFlag();
    setDismissed(true);
    if (ctx && welcomeRow) ctx.dismiss(welcomeRow.id);
  }, [ctx, welcomeRow]);

  const handleOpenActivity = useCallback(() => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event(ACTIVITY_DOCK_OPEN_EVENT));
    }
    handleDismiss();
  }, [handleDismiss]);

  useEffect(() => {
    if (!shouldShow) return;
    const timer = window.setTimeout(() => {
      handleDismiss();
    }, AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [shouldShow, handleDismiss]);

  if (!shouldShow) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="bb-welcome-banner-title"
      className="pointer-events-none fixed inset-x-0 bottom-6 z-[9400] flex justify-center px-4"
    >
      <div className="pointer-events-auto flex max-w-md items-start gap-3 rounded-2xl border border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 via-white to-amber-50 px-4 py-3 shadow-2xl ring-1 ring-fuchsia-300/40 dark:border-fuchsia-900 dark:from-fuchsia-950/60 dark:via-zinc-900 dark:to-amber-950/40">
        <div className="mt-0.5 shrink-0 rounded-full bg-fuchsia-100 p-2 dark:bg-fuchsia-900/60">
          <Sparkles className="h-5 w-5 text-fuchsia-700 dark:text-fuchsia-200" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p id="bb-welcome-banner-title" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Welcome to your first achievement
          </p>
          <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">
            You just earned <span className="font-medium">First Login</span>. Open Activity to see all the ways to earn more.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={handleOpenActivity}
              className="rounded-full bg-fuchsia-700 px-3 py-1 text-xs font-semibold text-white shadow-sm hover:bg-fuchsia-800 focus:outline-none focus:ring-2 focus:ring-fuchsia-400 dark:bg-fuchsia-600 dark:hover:bg-fuchsia-500"
            >
              Open Activity
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="rounded-full px-3 py-1 text-xs font-medium text-gray-700 hover:bg-white/60 focus:outline-none focus:ring-2 focus:ring-fuchsia-400 dark:text-gray-200 dark:hover:bg-zinc-800/60"
            >
              Dismiss
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Close welcome banner"
          className="shrink-0 rounded-full p-1 text-gray-500 hover:bg-white/60 focus:outline-none focus:ring-2 focus:ring-fuchsia-400 dark:text-gray-300 dark:hover:bg-zinc-800/60"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>,
    document.body,
  );
}
